import React, { useEffect } from 'react';
import { formatDate, Spacer, Button, Typography } from '@guildeducationinc/recess';
import '../EditProfile.scss';
import { ApolloError, useLazyQuery } from '@apollo/client';
import { useAuth } from '@guildeducationinc/guild-auth';
import { datadogRum } from '@datadog/browser-rum';
import {
  GET_USER_EMPLOYERS,
  GetUserEmployersResponse,
  UserEmployer,
} from '@src/graphql/profile_edit/queries/userEmployers';
import { Server } from '@src/components/GqlProvider/GqlProvider';
import { clickable } from '@src/analytics';
import Divider from '@src/components/Shared/Divider/Divider';
import './EmploymentInformation.scss';
import Skeleton from '@src/components/Shared/Skeleton/Skeleton';
import { setCookie } from '@src/utilities/cookieUtility';
import { VERIFICATION_URL, PUBLIC_URL, SALESFORCE_NEW_REQUEST_URL } from '@src/constants';

const EmployerCard: React.FC<{
  employerUuid: string;
  employeeId?: string;
  verifiedOn?: string;
  employerName?: string;
}> = ({ employerUuid, employeeId, verifiedOn, employerName }) => {
  return (
    <>
      <img
        className='employer-logo-wrapper margin--bottom_16'
        src={`https://recess-images.guildeducation.com/logos/${employerUuid}_color.svg`}
        alt={`${employerName} logo`}
      />
      <Typography variant='body1'>
        Employee ID: {employeeId === null ? 'none' : employeeId}{' '}
      </Typography>
      <Typography variant='body1'>
        <span className='padding'>{employerName}</span>
        {verifiedOn && <span className='vertLine'>Verified on {verifiedOn}</span>}
      </Typography>
    </>
  );
};

const EmploymentInformation: React.FC = () => {
  const { user } = useAuth();

  const onError = (error: ApolloError): void => {
    datadogRum.addError(error, {
      message: `There was an error getting employment information data: ${error.message}`,
      stack: error.stack,
    });
  };

  const [
    getUserEmployers,
    { error: userEmployersError, data: userEmployerData },
  ] = useLazyQuery<GetUserEmployersResponse>(GET_USER_EMPLOYERS, {
    context: {
      server: Server.EMPLOYMENT_SERVICE,
    },
    variables: {
      uuid: user?.id,
    },
    onError,
  });

  useEffect(() => {
    if (user?.id) {
      getUserEmployers();
    }
  }, [user, getUserEmployers]);

  const primaryEmployer = userEmployerData?.userEmployers.find(
    (employer: UserEmployer) => employer.primary === true
  );
  const previousEmployers = userEmployerData?.userEmployers.filter(
    (employer: UserEmployer) => employer.primary === false
  );

  const setRedirectToCookie = () => {
    const domainParts = window.location.hostname.split('.');
    const domain =
      domainParts.length === 1
        ? domainParts[0] // localhost
        : `${domainParts[domainParts.length - 2]}.${domainParts[domainParts.length - 1]}`;
    setCookie({
      name: 'redirectTo',
      value: `${PUBLIC_URL}/home/user_edit#employment-information`,
      domain,
    });
  };

  const changeClickable = () => {
    clickable({
      loggedIn: true,
      product: 'account',
      tag: 'changeEmployerButton',
      text: 'Change',
      type: 'button',
    });
  };

  return (
    <>
      <Typography variant='h3'>Employment information</Typography>
      <div className='padding--bottom_32 padding--top8 width--9/12'>
        These are the Guild partners you&apos;ve worked for.
      </div>
      <div className='employer-details padding--x_24 padding--bottom_24 margin--bottom_24 borderWidth--1 borderColor--black-lighten-50'>
        <div className='employer-details-heading'>
          <Typography className='currentEmployerHeading' variant='h4'>
            Current employer
          </Typography>
        </div>
        {userEmployersError && (
          <Typography>
            There was an error loading your employer information.
            <Typography>
              {' '}
              Try again or <a href={SALESFORCE_NEW_REQUEST_URL}>report a problem.</a>
            </Typography>
          </Typography>
        )}
        <Skeleton>
          {primaryEmployer && (
            <div className='flex flex-direction--row flexJustify--space_between flexItems--center'>
              <div>
                <EmployerCard
                  employerUuid={primaryEmployer?.employerId}
                  employeeId={primaryEmployer?.employeeId}
                  verifiedOn={formatDate(primaryEmployer?.verifiedOn, 'MM/dd/yyyy')}
                  employerName={primaryEmployer?.employer?.name}
                />
              </div>
              <Button
                appearance='ghost'
                href={VERIFICATION_URL}
                onClick={() => {
                  setRedirectToCookie();
                  changeClickable();
                }}
              >
                Change
              </Button>
            </div>
          )}
        </Skeleton>
      </div>
      {previousEmployers && previousEmployers.length >= 1 && (
        <div className='employer-details padding--x_24 padding--bottom_24 margin--bottom_24 borderWidth--1 borderColor--black-lighten-50'>
          <div className='employer-details-heading'>
            <Typography className='currentEmployerHeading' variant='h4'>
              Previous employers
            </Typography>
          </div>
          <Skeleton>
            {primaryEmployer &&
              previousEmployers
                ?.sort((userEmployerA: UserEmployer, userEmployerB: UserEmployer) => {
                  if (userEmployerA.verifiedOn && userEmployerB.verifiedOn) {
                    return (
                      new Date(userEmployerB.verifiedOn).getTime() -
                      new Date(userEmployerA.verifiedOn).getTime()
                    );
                  }
                  return 0;
                })
                .map((userEmployer: UserEmployer, index: number) => (
                  <div key={userEmployer.employerId}>
                    <EmployerCard
                      key={userEmployer.employerId}
                      employerUuid={userEmployer.employerId}
                      employeeId={userEmployer.employeeId}
                      verifiedOn={formatDate(userEmployer.verifiedOn, 'MM/dd/yyyy')}
                      employerName={userEmployer.employer?.name}
                    />
                    {previousEmployers?.length - 1 === index ? null : (
                      <Spacer py={24}>
                        <Divider color='black-lighten-87' />
                      </Spacer>
                    )}
                  </div>
                ))}
          </Skeleton>
        </div>
      )}
    </>
  );
};

export default EmploymentInformation;
